import React from 'react'
import styled from 'styled-components'
import { media } from 'src/styles/styled-components'
import PropTypes from 'prop-types'
import ArBaseImage from 'src/components/ArBaseImage/ArBaseImage'
import ArBaseContainer from 'src/components/ArBaseContainer/ArBaseContainer'
import ArTextWithImageHeading from 'src/components/typography/ArTextWithImageHeading'
import ArTextWithImageDescription from 'src/components/typography/ArTextWithImageDescription'
import { generateScProps, deviceInfoProvider, generateJSX } from 'src/utils'
import ArAnimation from 'src/components/ArAnimation/ArAnimation'

const StyledWrapper = styled.section`
  margin-bottom: ${ p => p.scComponentMargins.bottom[0] };
  margin-top: ${ p => p.scComponentMargins.top[0] };
  overflow: hidden;

  ${ media.tablet`
    margin-bottom: ${ p => p.scComponentMargins.bottom[1] };
    margin-top: ${ p => p.scComponentMargins.top[1] };
  ` }

  ${ media.desktop`
    margin-bottom: ${ p => p.scComponentMargins.bottom[2] };
    margin-top: ${ p => p.scComponentMargins.top[2] };
  ` }
`

const StyledContainer = styled(ArBaseContainer)`
  ${ media.minWidthTablet`
    display: flex;
    flex-direction: ${ p => !p.scInverseStack ? 'column' : 'column-reverse' };
  ` }

  ${ media.minWidthDesktop`
    display: flex;
    align-items: center;
    flex-direction: ${ p => !p.scInverseStack ? p.scImageToRight ? 'row-reverse' : 'row' : 'column-reverse' };
  ` }
`

const StyledImage = styled(ArBaseImage)`
  margin-left: ${ p => p.scImageMargins.left[0] };
  margin-bottom: ${ p => p.scImageMargins.bottom[0] };
  margin-top: ${ p => p.scImageMargins.top && p.scImageMargins.top[0] };
  margin-right: ${ p => p.scImageMargins.right[0] };

  ${ media.tablet`
    margin-left: ${ p => p.scImageMargins.left[1] };
    margin-bottom: ${ p => p.scImageMargins.bottom[1] };
    margin-top: ${ p => p.scImageMargins.top && p.scImageMargins.top[1] };
    margin-right: ${ p => p.scImageMargins.right[1] };
  ` }

  ${ media.desktop`
    margin-left: ${ p => p.scImageMargins.left[2] };
    margin-bottom: ${ p => p.scImageMargins.bottom[2] };
    margin-top: ${ p => p.scImageMargins.top && p.scImageMargins.top[2] };
    margin-right: ${ p => p.scImageMargins.right[2] };
    width: ${ p => p.scImageWidthOnDesktop || '53%' };
    align-self: ${ p => p.scImageOnEdge && 'flex-end' };
  ` }
`

const StyledAnimationWrapper = styled.div`
`

const StyledTextWrapper = styled.div`

  ${ media.desktop`
    width: ${ p => p.scTextWrapperMaxWidthOnDesktop ? '100%' : '40%' };
    max-width: ${ p => p.scTextWrapperMaxWidthOnDesktop };
    margin-right: ${ p => p.scImageToRight && !p.scInverseStack ? (p.scDesktopGap / p.theme.gridWidth * 100 + '%') : 0 };
    margin-left: ${ p => !p.scImageToRight && !p.scInverseStack ? (p.scDesktopGap / p.theme.gridWidth * 100 + '%') : 0 };
    padding-bottom: ${ p => p.scImageOnEdge && '30px' };
  ` }
`

const StyledTitle = styled(ArTextWithImageHeading)`
  margin-bottom: ${ p => (p.scTitleBottomMargins && p.scTitleBottomMargins[0]) || '35px' };
  text-align: center;

  ${ media.tablet`
    margin-bottom: ${ p => (p.scTitleBottomMargins && p.scTitleBottomMargins[1]) || '30px' };
    font-size: ${ p => p.scBigHeadingOnTablet && '45px' };
    line-height: ${ p => p.scBigHeadingOnTablet && 1.2 };
  ` }

  ${ media.desktop`
    margin-bottom: ${ p => (p.scTitleBottomMargins && p.scTitleBottomMargins[2]) || '30px' };
    width: ${ p => !p.scCenterTextOnDesktop ? '105%' : 'auto' };
    text-align: ${ p => p.scCenterTextOnDesktop ? 'center' : 'left' };
  ` }
`

const StyledDescription = styled(ArTextWithImageDescription)`
  text-align: center;
  max-width: ${ p => (p.scDescMaxWidth && p.scDescMaxWidth[0]) || 'none' };

  ${ media.tablet`
    max-width: ${ p => (p.scDescMaxWidth && p.scDescMaxWidth[1]) || '500px' };
    margin-left: auto;
    margin-right: auto;
  ` }

  ${ media.desktop`
    max-width: ${ p => (p.scDescMaxWidth && p.scDescMaxWidth[2]) || '500px' };
    text-align: ${ p => p.scCenterTextOnDesktop ? 'center' : 'left' };
  ` }
`

class ArTextWithImageStacked extends React.Component {
  static propTypes = {
    images: PropTypes.arrayOf(PropTypes.shape({ url: PropTypes.string })),
    title: PropTypes.oneOfType([ PropTypes.string, PropTypes.array ]),
    description: PropTypes.oneOfType([ PropTypes.string, PropTypes.array ]),
    scImageToRight: PropTypes.bool,
    scImageOnEdge: PropTypes.bool,
    scImageMargins: PropTypes.shape({
      right: PropTypes.arrayOf(PropTypes.string), // [ 'mobile', 'tablet', 'desktop' ]
      bottom: PropTypes.arrayOf(PropTypes.string),
      left: PropTypes.arrayOf(PropTypes.string),
    }),
    scComponentMargins: PropTypes.shape({
      top: PropTypes.arrayOf(PropTypes.string),
      bottom: PropTypes.arrayOf(PropTypes.string),
    }),
    scDesktopGap: PropTypes.number, // px
    scBigHeadingOnTablet: PropTypes.bool,
    scImageWidthOnDesktop: PropTypes.string,
    scHideMobileImg: PropTypes.bool,
    scTitleBottomMargins: PropTypes.arrayOf(PropTypes.string), // ['mobile', 'tablet', 'desktop']
    scInverseStack: PropTypes.bool,
    scDescMaxWidth: PropTypes.array, // ['mobile', 'tablet', 'desktop']
    animation: PropTypes.shape({
      id: PropTypes.string,
    }),
    scCenterTextOnDesktop: PropTypes.bool,
  }

  get deviceImage () {
    if (!this.props.images) return null
    if (this.props.device.isDesktop) return this.props.images[2]
    else if (this.props.device.isTablet) return this.props.images[1]
    else if (this.props.device.isMobile) return this.props.images[0]
    else return {}
  }

  get hideImage () {
    return this.props.device.isMobile && this.props.scHideMobileImg
  }

  render () {
    const { title, description, animation } = this.props
    const scProps = generateScProps(this.props)
    const image = this.deviceImage

    return (
      <StyledWrapper {...scProps}>
        <StyledContainer {...scProps}>
          { !this.hideImage && image && <StyledImage src={image.url} alt={image.alt} {...scProps}></StyledImage>}
          { animation && <StyledAnimationWrapper {...scProps}><ArAnimation animation={animation} /></StyledAnimationWrapper>}
          <StyledTextWrapper {...scProps}>
            <StyledTitle {...scProps}>{ generateJSX(title) }</StyledTitle>
            <StyledDescription {...scProps}>{ generateJSX(description) }</StyledDescription>
          </StyledTextWrapper>
        </StyledContainer>
      </StyledWrapper>
    )
  }
}

export default deviceInfoProvider(ArTextWithImageStacked)
